import React, {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {KTSVG} from '../../../../_metronic/helpers'
import Moment from 'react-moment'
import {useSelector, shallowEqual} from 'react-redux'
import {RootState} from '../../../../setup'
import {TenantTypeIds} from '../../../common/constants/tenantTypeIds'
import {toMoneyFormat} from '../../../common/helpers/stringHelper'
import {WorkOrderClient, WorkOrderQueryParams} from '../../../infrastracture/api/WorkOrderClient'
import {OverlayTrigger, Tooltip} from 'react-bootstrap-v5'
import {DataTable, ColumnDef} from '../../../common/components/Table'

interface Props {
  parentWorkOrderId: string
}

const WorkOrderChildren: React.FC<Props> = (props) => {
  const {parentWorkOrderId} = props

  const user: any = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as any

  const [workOrders, setWorkOrders] = useState<any>([])

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalRecords: 1,
    totalPages: 1,
    skip: 0,
    take: 50,
    reset: true,
    sort: 'name',
    direction: 'asc',
  })

  // Helper function to calculate cost from charges
  const calculateCost = (workOrder: any): number => {
    if (!workOrder.charges || workOrder.charges.length === 0) return 0
    return workOrder.charges
      .filter((c: any) => c.isActive)
      .reduce((sum: number, charge: any) => sum + (charge.value || 0), 0)
  }

  // Helper function to calculate cost from approved quotes
  const calculateApprovedQuotesCost = (workOrder: any): number => {
    if (!workOrder.quotes || workOrder.quotes.length === 0) return 0
    return workOrder.quotes
      .filter((q: any) => q.quoteStatus?.name?.toLowerCase() === 'approved')
      .reduce((sum: number, quote: any) => sum + (quote.estimate || 0), 0)
  }

  useEffect(() => {
    if (pagination.reset && parentWorkOrderId) fetchData()
  }, [pagination])

  useEffect(() => {
    fetchData()
  }, [parentWorkOrderId])

  async function fetchData() {
    setLoading(true)
    try {
      let query = new WorkOrderQueryParams()

      let response = await WorkOrderClient.List(
        query
          .Paginate(pagination.skip, pagination.take)
          .WithParam('ShowAllWorkOrdersForChildView', true)
          .WithParentWorkOrderId(parentWorkOrderId)
          .Sort(pagination.sort, pagination.direction)
      )

      if (!response.authenticated) {
      } else {
        const {data} = response
        setWorkOrders(data?.data)

        const totalPages = Math.ceil(data.total / pagination.take)

        setPagination({
          ...pagination,
          totalPages,
          totalRecords: data.total,
          reset: false,
        })
      }
      setLoading(false)
    } catch (e) {
      console.error(e)
    }
  }

  const [loading, setLoading] = useState(false)

  // Define columns for DataTable
  const columns: ColumnDef<any>[] = [
    {
      header: 'WO #',
      static: true,
      cell: (item) => (
        <OverlayTrigger
          placement='right'
          trigger={['hover', 'focus']}
          overlay={
            <Tooltip id={`wo-${item.id}-created-info`}>
              Created: {item.created && (
                <Moment utc local format='ddd DD MMM, YYYY h:mm a'>
                  {item.created}
                </Moment>
              )}
              {!item.created && <span>Not specified</span>}
              <br />
              By: {item.createdBy?.name && item.createdBy?.surname
                ? `${item.createdBy?.name} ${item.createdBy?.surname}`
                : 'Not specified'}
            </Tooltip>
          }
        >
          <Link className='text-dark fw-bolder mb-1 fs-6' to={`/work-orders/details/${item.id}`}>
            {item.workOrderNumber ? `${item.workOrderNumber}` : <i>Not specified</i>}
          </Link>
        </OverlayTrigger>
      ),
    },
    {
      header: 'Site',
      static: true,
      cell: (item) => <span className='text-dark fw-bolder mb-1 fs-6'>{item.site?.name}</span>,
    },
    {
      header: 'Contractor',
      static: true,
      cell: (item) => <span className='text-dark fw-bolder mb-1 fs-6'>{item.assignedToTenant?.name}</span>,
    },
    {
      header: 'Status',
      static: true,
      cell: (item) => (
        <span className='text-dark fw-bolder mb-1 fs-6'>{item?.workOrderStatus?.name}</span>
      ),
    },
    {
      header: 'Type',
      static: true,
      cell: (item) => (
        <span className='text-dark fw-bolder mb-1 fs-6'>{item.workOrderType?.name}</span>
      ),
    },
    {
      header: 'Category',
      static: true,
      cell: (item) => (
        <span className='text-dark fw-bolder mb-1 fs-6'>{item.workOrderCategory?.name}</span>
      ),
    },
    {
      header: 'Sell',
      static: true,
      cell: (item) => {
        const calculatedCost = calculateCost(item)
        return (
          <span className='text-dark fw-bolder mb-1 fs-6'>
            {calculatedCost > 0 ? toMoneyFormat(calculatedCost) : '$0'}
          </span>
        )
      },
    },
    {
      header: 'Cost',
      static: true,
      cell: (item) => {
        // Use backend-calculated cost (sum of approved quotes), fallback to client-side calculation
        const cost =
          item.cost !== null && item.cost !== undefined
            ? item.cost
            : calculateApprovedQuotesCost(item)

        return (
          <>
            {item.isParent ? (
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip id={`cost-${item.id}`}>Total of approved quotes</Tooltip>
                }
              >
                <span className='text-dark fw-bolder mb-1 fs-6'>
                  {cost > 0 ? toMoneyFormat(cost) : '$0'}
                  {cost > 0 && (
                    <KTSVG
                      path='/media/icons/duotune/general/gen045.svg'
                      className='svg-icon-7 ms-1'
                    />
                  )}
                </span>
              </OverlayTrigger>
            ) : (
              <span className='text-dark fw-bolder mb-1 fs-6'>
                {cost > 0 ? toMoneyFormat(cost) : '$0'}
              </span>
            )}
          </>
        )
      },
    },
    {
      header: '',
      static: true,
      className: 'text-end',
      cell: (item) => (
        <Link
          className='btn btn-bg-light btn-color-muted btn-active-color-primary btn-sm px-4 me-2'
          to={`/work-orders/details/${item.id}`}
        >
          Details
        </Link>
      ),
    },
  ]

  return (
    <>
      <div className={`card`}>
        <div className='card-header'>
          <div className='card-title'>
            <div className='d-flex align-items-center position-relative my-1'>
              <h2 className='text-gray-600'>Work Order Project</h2>
              {loading && (
                <div className='d-flex align-items-center'>
                  <span className='spinner-border spinner-border-lg align-middle ms-2'></span>
                </div>
              )}
            </div>
          </div>

          <div className='card-toolbar'>
            <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
              {user.tenant?.tenantTypeId?.toUpperCase() === TenantTypeIds.Owner && (
                <Link
                  className='btn btn-bg-light btn-color-muted btn-active-color-primary btn-sm px-4 me-2'
                  to={`/work-orders/add?parentId=${parentWorkOrderId}`}
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                  Add
                </Link>
              )}
            </div>
          </div>
        </div>
        <div className='card-body py-3'>
          <DataTable
            data={workOrders}
            columns={columns}
            loading={loading}
            emptyMessage='No work orders found'
            sortable={true}
            sort={pagination.sort}
            direction={pagination.direction as 'asc' | 'desc'}
            onSortChange={(sort, direction) => {
              setPagination({
                ...pagination,
                sort,
                direction,
                reset: true,
              })
            }}
            pagination={{
              currentPage: pagination.currentPage,
              totalPages: pagination.totalPages,
              totalRecords: pagination.totalRecords,
              take: pagination.take,
              onPageChange: (page) => {
                setPagination({
                  ...pagination,
                  currentPage: page,
                  skip: page === 1 ? 0 : (page - 1) * pagination.take,
                  reset: true,
                })
              },
              onPageSizeChange: (pageSize) => {
                setPagination({
                  ...pagination,
                  take: pageSize,
                  reset: true,
                  currentPage: 1,
                })
              },
            }}
            rowKey='id'
          />
        </div>
      </div>
    </>
  )
}

export default WorkOrderChildren
